/** Role helpers over the Cognito ID token. */
import type { User } from "oidc-client-ts";

/** Cognito groups, mirroring the workpaper Cover Sheet sign-off rows.
 *  Defined here rather than in config so that importing auth helpers does not
 *  drag in runtime configuration validation. */
export const ROLES = {
  admin: "Admin",
  preparer: "Preparer",
  reviewer: "Reviewer",
  director: "DirectorApproval",
} as const;

/**
 * Groups from the ID token.
 *
 * Cognito emits `cognito:groups` as a JSON array in the token itself; the
 * bracketed-string shapes API Gateway produces do not occur here, but the
 * array is still defensively normalised because an absent claim is normal for
 * a user an admin has not assigned a role to yet.
 */
export function groupsOf(user: User | null | undefined): string[] {
  const raw = user?.profile?.["cognito:groups"];
  if (Array.isArray(raw)) return raw.map(String);
  if (typeof raw === "string" && raw.length > 0) return [raw];
  return [];
}

export function hasRole(user: User | null | undefined, role: string): boolean {
  return groupsOf(user).includes(role);
}

export function isAdmin(user: User | null | undefined): boolean {
  return hasRole(user, ROLES.admin);
}

/** A user with no group cannot do anything, and should be told why. */
export function hasAnyRole(user: User | null | undefined): boolean {
  return groupsOf(user).length > 0;
}

export function displayName(user: User | null | undefined): string {
  const profile = user?.profile;
  return String(profile?.email ?? profile?.["cognito:username"] ?? "Signed in");
}

// ---------------------------------------------------------------------------
// Resuming a session after a refresh
// ---------------------------------------------------------------------------

/**
 * Marks that a promptless sign-in has already been tried in this tab.
 *
 * A flag, not a token: the point of keeping tokens in memory is that nothing
 * readable by an injected script survives the page, and "we already asked"
 * is not worth reading.
 */
const ATTEMPTED = "casa.resume-attempted";

/** Session storage, or nothing, in a browser that refuses it. */
function flags(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

export function resumeAttempted(): boolean {
  return flags()?.getItem(ATTEMPTED) === "1";
}

export function markResumeAttempted(): void {
  flags()?.setItem(ATTEMPTED, "1");
}

export function clearResumeAttempt(): void {
  flags()?.removeItem(ATTEMPTED);
}

// ---------------------------------------------------------------------------
// Arriving from Microsoft's My Apps
// ---------------------------------------------------------------------------

/** The Cognito identity provider's name, as Terraform creates it. */
export const MICROSOFT_PROVIDER = "Microsoft";

/**
 * Whether this visit came from the BAS tile in My Apps, which links to
 * `?signin=microsoft`.
 *
 * Someone who clicks that tile is already signed in to Microsoft, so the
 * sign-in page and its "Sign in with Microsoft" button are two clicks that
 * decide nothing. Asking Cognito for that provider directly skips both.
 */
export function arrivedFromMyApps(search: string = window.location.search): boolean {
  return new URLSearchParams(search).get("signin") === "microsoft";
}

/**
 * Whether Cognito told us there is no session to resume.
 *
 * `prompt=none` asks for tokens without showing anything. When no hosted
 * session exists Cognito answers with an OAuth error rather than a login
 * form, and that is an expected outcome -- it means "ask them to sign in",
 * not "something broke". Anything else is a real failure worth showing.
 */
export function isNoSessionError(error: Error | null | undefined): boolean {
  if (!error) return false;
  const text = `${error.name} ${error.message}`.toLowerCase();
  return (
    text.includes("login_required") ||
    text.includes("interaction_required") ||
    text.includes("consent_required") ||
    text.includes("account_selection_required")
  );
}
